import React from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'

const eurFormatter = new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' })
const pctFormatter = new Intl.NumberFormat('fi-FI', { style: 'percent', maximumFractionDigits: 1 })

const numCellSx = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' }

const deltaColor = (n) => (n > 0 ? '#28a745' : n < 0 ? '#dc3545' : '#6c757d')

const CategoryBreakdownTable = ({ title = 'Kategoriakohtaiset luvut', data, loading, currentLabel = 'Tämä viikko', previousLabel = 'Viime viikko' }) => {
  const safe = Array.isArray(data) ? data : []
  const totals = safe.reduce((acc, r) => {
    acc.current += Number(r.current) || 0
    acc.previous += Number(r.previous) || 0
    return acc
  }, { current: 0, previous: 0 })
  const totalDelta = totals.current - totals.previous
  const totalDeltaPct = totals.previous === 0 ? null : totalDelta / totals.previous

  return (
    <Card sx={{ borderRadius: 2, height: '100%' }}>
      <CardContent sx={{ p: '12px 14px', '&:last-child': { pb: '12px' } }}>
        <Typography variant='caption' sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3 }}>
          {title}
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180 }}>
            <CircularProgress size={20} />
          </Box>
        ) : safe.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180, color: 'text.secondary', fontSize: 12 }}>
            Ei dataa
          </Box>
        ) : (
          <Table size='small' sx={{ mt: 1, '& td, & th': { px: 1, py: 0.5, fontSize: 12 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Kategoria</TableCell>
                <TableCell sx={numCellSx}>{currentLabel}</TableCell>
                <TableCell sx={numCellSx}>{previousLabel}</TableCell>
                <TableCell sx={numCellSx}>Muutos €</TableCell>
                <TableCell sx={numCellSx}>Muutos %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {safe.map((r) => {
                const delta = (Number(r.current) || 0) - (Number(r.previous) || 0)
                const deltaPct = r.previous === 0 ? null : delta / r.previous
                return (
                  <TableRow key={r.category}>
                    <TableCell>{(r.category || '—').toLocaleUpperCase('fi-FI')}</TableCell>
                    <TableCell sx={numCellSx}>{eurFormatter.format(r.current || 0)}</TableCell>
                    <TableCell sx={numCellSx}>{eurFormatter.format(r.previous || 0)}</TableCell>
                    <TableCell sx={{ ...numCellSx, color: deltaColor(delta) }}>
                      {(delta > 0 ? '+' : delta < 0 ? '−' : '') + eurFormatter.format(Math.abs(delta))}
                    </TableCell>
                    <TableCell sx={{ ...numCellSx, color: deltaColor(delta) }}>
                      {deltaPct === null ? '—' : (delta > 0 ? '+' : delta < 0 ? '−' : '') + pctFormatter.format(Math.abs(deltaPct))}
                    </TableCell>
                  </TableRow>
                )
              })}
              <TableRow sx={{ '& td': { fontWeight: 600, borderTop: '1px solid #e0e0e0' } }}>
                <TableCell>Yhteensä</TableCell>
                <TableCell sx={numCellSx}>{eurFormatter.format(totals.current)}</TableCell>
                <TableCell sx={numCellSx}>{eurFormatter.format(totals.previous)}</TableCell>
                <TableCell sx={{ ...numCellSx, color: deltaColor(totalDelta) }}>
                  {(totalDelta > 0 ? '+' : totalDelta < 0 ? '−' : '') + eurFormatter.format(Math.abs(totalDelta))}
                </TableCell>
                <TableCell sx={{ ...numCellSx, color: deltaColor(totalDelta) }}>
                  {totalDeltaPct === null ? '—' : (totalDelta > 0 ? '+' : totalDelta < 0 ? '−' : '') + pctFormatter.format(Math.abs(totalDeltaPct))}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

export default CategoryBreakdownTable
